import { utils } from 'xlsx'

import csvReport from './csvReport'

const isUpperCase = (string) => /^[A-Z]*$/.test(string.split(' ').join(''))

const formatData = (data, from, sort, intl, columnConfig) => {
  const formatter = csvReport[from]

  if (formatter) {
    return formatter(data, sort ?? {}, intl, columnConfig)
  }

  return data.map((row) =>
    Object.entries(row).reduce((result, [key, value]) => {
      const updatedValue = Array.isArray(value) ? value.join(',') : value

      return {
        ...result,
        [isUpperCase(key)
          ? key
          : key
              .match(/([A-Z]?[^A-Z]*)/g)
              .slice(0, -1)
              .join(' ')
              .toUpperCase()]: updatedValue,
      }
    }, {}),
  )
}

// `emptyHeaderTemplate` is an optional row shape (keys in column order) used only when there are no
// rows and no `csvReport` formatter, so generic exports can still emit a header-only file.
const getHeaderSourceRow = (rawData, from, sort, intl, columnConfig, emptyHeaderTemplate) => {
  const data = formatData(rawData, from, sort, intl, columnConfig)

  if (data?.length) {
    return data[0]
  }

  const formatter = csvReport[from]
  if (!formatter) {
    return emptyHeaderTemplate ? formatData([emptyHeaderTemplate], from, sort, intl, columnConfig)[0] : null
  }

  return formatter([{}], sort ?? {}, intl, columnConfig)[0] ?? null
}

export const buildSheet = (rawData, from, sort, intl, columnConfig, emptyHeaderTemplate) => {
  const data = formatData(rawData, from, sort, intl, columnConfig)
  const ws = utils.json_to_sheet(data, { skipHeader: false })
  const wb = { Sheets: { data: ws }, SheetNames: ['data'] }
  const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'] // if more are needed add more

  // Assigned headers names in order (A1, B1, C1, ...)
  const headerSource = getHeaderSourceRow(rawData, from, sort, intl, columnConfig, emptyHeaderTemplate)
  if (headerSource) {
    const headerKeys = Object.keys(headerSource).map((defaultMessage) => {
      if (
        from === 'COMPLIANCE_AUDIT_REPORT' &&
        defaultMessage === 'compliance' &&
        columnConfig?.complianceColumnLabel
      ) {
        return columnConfig.complianceColumnLabel
      }

      // The generic header formatter splits camelCase keys with a regex that also swallows any space
      // already present in a label, so Title-Case labels (e.g. "First Name") come through with a
      // double space. Collapse runs of whitespace to a single space so headers read correctly.
      const header = intl
        ? intl.formatMessage({ defaultMessage, id: `EXPORT.${from}.${defaultMessage.toUpperCase()}` })
        : defaultMessage
      return header.replace(/\s+/g, ' ').trim()
    })

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < headerKeys.length; i++) {
      const cellRef = `${letters[i]}1`
      ws[cellRef] = { t: 's', v: headerKeys[i] }
    }

    if (!data.length || !ws['!ref']) {
      ws['!ref'] = `A1:${letters[headerKeys.length - 1]}1`
    }
  }

  return { ws, wb }
}

export const getCsvBlob = (data, from, sort, intl, columnConfig, emptyHeaderTemplate) => {
  const { ws } = buildSheet(data, from, sort, intl, columnConfig, emptyHeaderTemplate)
  const csvContent = utils.sheet_to_csv(ws)

  return new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
}
